
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileDown } from "lucide-react";
import { Association, Property } from "@/models";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PropertyBulkFormContentProps {
  associations: Association[];
  isLoading: boolean;
  onSave: (properties: Omit<Property, "id" | "created_at" | "updated_at">[]) => void;
  showAssociationField?: boolean;
  associationId?: number;
}

interface BulkProperty {
  address: string;
  suite_unit?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  latitude?: string;
  longitude?: string;
}

const PropertyBulkFormContent: React.FC<PropertyBulkFormContentProps> = ({
  associations,
  isLoading,
  onSave,
  showAssociationField = true,
  associationId,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedAssociationId, setSelectedAssociationId] = useState<number | undefined>(associationId);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedCsvEntries, setParsedCsvEntries] = useState<BulkProperty[]>([]);
  const { toast } = useToast();

  const filteredAssociations = searchValue
    ? associations.filter(association => 
        association.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : associations;

  const parseCsvFile = async (file: File): Promise<BulkProperty[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const csvText = event.target?.result as string;
          const lines = csvText.split('\n').filter(line => line.trim().length > 0);
          
          // Skip header row if it exists
          const dataLines = lines[0].toLowerCase().includes('address') || 
                           lines[0].toLowerCase().includes('suite') ? 
                           lines.slice(1) : lines;
          
          const parsedEntries = dataLines.map(line => {
            // Split by comma, handling for quoted values
            const parts = line.split(',').map(part => part.trim().replace(/^"|"$/g, ''));
            
            return {
              address: parts[0] || '',
              suite_unit: parts[1] || '',
              city: parts[2] || '',
              state: parts[3] || '',
              zip_code: parts[4] || '',
              latitude: parts[5] || '',
              longitude: parts[6] || ''
            };
          }).filter(entry => entry.address); // Only include entries with addresses
          
          resolve(parsedEntries);
        } catch (error) {
          console.error("Error parsing CSV:", error);
          reject("Failed to parse CSV file. Please make sure it's correctly formatted.");
        }
      };
      
      reader.onerror = () => {
        reject("Error reading the CSV file.");
      };
      
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file format",
        description: "Please upload a CSV file",
        variant: "destructive"
      });
      return;
    }
    
    setCsvFile(file);
    
    try {
      const entries = await parseCsvFile(file);
      setParsedCsvEntries(entries);
      toast({
        title: "CSV file parsed successfully",
        description: `Found ${entries.length} properties`,
      });
    } catch (error) {
      toast({
        title: "Error parsing CSV file",
        description: error as string,
        variant: "destructive"
      });
    }
  };

  const handleAssociationChange = (value: string) => {
    const newAssociationId = parseInt(value);
    setSelectedAssociationId(newAssociationId);
  };

  const handleDownloadSampleCsv = () => {
    const csvContent = "Address,Suite/Unit,City,State,Zip,Latitude,Longitude\n123 Main St,101,New York,NY,10001,40.7128,-74.0060\n456 Elm St,202,Chicago,IL,60007,41.8781,-87.6298\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'properties_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = () => {
    const effectiveAssociationId = selectedAssociationId || associationId;
    
    if (!effectiveAssociationId) {
      toast({
        title: "Association required",
        description: "Please select an association before adding properties",
        variant: "destructive"
      });
      return;
    }

    const csvProps = parsedCsvEntries.map(entry => ({
      address: entry.address,
      suite_unit: entry.suite_unit || null,
      city: entry.city || null,
      state: entry.state || null,
      zip_code: entry.zip_code || null,
      latitude: entry.latitude ? parseFloat(entry.latitude) : null,
      longitude: entry.longitude ? parseFloat(entry.longitude) : null,
      association_id: effectiveAssociationId
    }));
    
    if (csvProps.length === 0) {
      toast({
        title: "No properties to add",
        description: "Please upload a CSV file with property data",
        variant: "destructive"
      });
      return;
    }

    // Validate that all properties have an address
    const invalidProps = csvProps.filter(prop => !prop.address);
    if (invalidProps.length > 0) {
      toast({
        title: "Missing addresses",
        description: `${invalidProps.length} properties are missing addresses`,
        variant: "destructive"
      });
      return;
    }

    onSave(csvProps);
  };

  const selectedAssociation = associations.find(a => a.id === (selectedAssociationId || associationId));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk Add Properties</CardTitle>
        <CardDescription>
          Add multiple properties to an association at once
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {showAssociationField && (
          <div className="space-y-2">
            <div className="space-y-2">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    {(selectedAssociationId || associationId)
                      ? selectedAssociation?.name || "Select association..."
                      : "Select association..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput 
                      placeholder="Search associations..." 
                      className="h-9" 
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                    <CommandEmpty>No association found.</CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-y-auto">
                      {filteredAssociations.map((association) => (
                        <CommandItem
                          key={association.id}
                          value={association.id.toString()}
                          onSelect={(value) => {
                            handleAssociationChange(value);
                            setOpen(false);
                            setSearchValue("");
                          }}
                        >
                          {association.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium">CSV Upload</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Upload a CSV file with property data
            </p>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="max-w-md"
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleDownloadSampleCsv}
                  title="Download sample CSV template"
                >
                  <FileDown className="h-4 w-4" />
                </Button>
              </div>
              
              <Alert>
                <AlertDescription>
                  CSV should be formatted with columns: Suite/Unit, Address, City, State, Zip (one property per row).
                </AlertDescription>
              </Alert>
              
              {parsedCsvEntries.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Successfully parsed {parsedCsvEntries.length} properties from CSV
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t pt-4 flex justify-end">
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Properties
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyBulkFormContent;
